import React, { Component } from 'react';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import Collapse from '@material-ui/core/Collapse';
import Popover from '@material-ui/core/Popover';
import CircularProgress from '@material-ui/core/CircularProgress';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import xRange from 'highcharts/modules/xrange.js';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import UpdateIcon from '@material-ui/icons/Update';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Portlet from 'components/Portlet';
import PortletHeader from 'components/PortletHeader';
import PortletLabel from 'components/PortletLabel';
import PortletToolbar from 'components/PortletToolbar';
import PortletContent from 'components/PortletContent';
import PortletFooter from 'components/PortletFooter';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import HelpIcon from '@material-ui/icons/Help';
import Period from '../../../../helpers/Period';
import {
    getFlindex,
    getFlindexCustom,
    flindexConfigFingerprint,
    FLINDEX_CONFIG_CHANGED_EVENT,
    FLINDEX_CONFIG_STORAGE_KEY,
} from '../../../../services/getFlindex';
import Box from '@material-ui/core/Box';
import moment from 'moment-timezone';
import 'moment/locale/es';
import styles from './styles';
import { reportFetchError } from '../../../../observability/rum';

highchartsMore(Highcharts);
xRange(Highcharts);
moment.locale('es');

// ─── FLINDEX constants ───
const FLINDEX_COLORS = {
    uptime:   '#41B900',
    downtime: '#C2272D',
    flapping: '#8000ff',
};
const REFRESH_TIME = 300000;

// ─── Tooltip labels ───
const TOOLTIP_LABELS = {};
TOOLTIP_LABELS[FLINDEX_COLORS.uptime] = 'Uptime';
TOOLTIP_LABELS[FLINDEX_COLORS.downtime] = 'Downtime';
TOOLTIP_LABELS[FLINDEX_COLORS.flapping] = 'Flapping (uptime inestable)';



// ─── Helpers ───
function toUTC(d) {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(),
                    d.getHours(), d.getMinutes(), d.getSeconds());
}

function formatDuration(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(hours + 'h');
    if (minutes > 0) parts.push(minutes + 'm');
    if (seconds > 0 || parts.length === 0) parts.push(seconds + 's');
    return parts.join(' ');
}



function buildSeries(segments, yIndex, pointWidth) {
    const seriesData = { uptime: [], downtime: [], flapping: [] };
    segments.forEach(seg => {
        const from = new Date(seg.from);
        const to = new Date(seg.to);
        seriesData[seg.color].push({
            x: toUTC(from),
            x2: toUTC(to),
            y: yIndex,
            color: FLINDEX_COLORS[seg.color],
        });
    });
    return [
        { name: 'Uptime',   pointWidth, color: FLINDEX_COLORS.uptime,   data: seriesData.uptime,   showInLegend: yIndex === 0 },
        { name: 'Downtime', pointWidth, color: FLINDEX_COLORS.downtime, data: seriesData.downtime, showInLegend: yIndex === 0 },
        { name: 'Flapping', pointWidth, color: FLINDEX_COLORS.flapping, data: seriesData.flapping, showInLegend: yIndex === 0 },
    ].filter(s => s.data.length > 0);
}

function makeXAxisConfig() {
    return {
        type: 'datetime',
        gridLineWidth: 0,
        dateTimeLabelFormats: {
            second: '%l:%M:%S%P',
            minute: '%l:%M%P',
            hour: '%H:%M',
            day: '<br/><b>%e/%b</b>',
            week: '%e. %b',
            month: '%b %y',
            year: '%Y',
        },
    };
}

function makeTooltipConfig() {
    return {
        useHTML: true,
        formatter: function () {
            var label = TOOLTIP_LABELS[this.point.color] || 'Desconocido';
            var from = Highcharts.dateFormat('%d/%m/%Y %H:%M', this.point.x);
            var to = Highcharts.dateFormat('%d/%m/%Y %H:%M', this.point.x2);
            var duration = formatDuration(this.point.x2 - this.point.x);
            return '<div style="padding:6px">' +
                '<b>Estado: ' + label + '</b><br/>' +
                'Desde: ' + from + '<br/>' +
                'Hasta: ' + to + '<br/>' +
                'Duración: ' + duration + '</div>';
        },
    };
}

function makePlotOptions() {
    return {
        series: {
            borderRadius: '0px',
            borderColor: 'transparent',
            turboThreshold: 30000,
            boostThreshold: 1,
            grouping: false,
        },
        xrange: {
            dataLabels: { enabled: false },
            borderRadius: 0,
            grouping: false,
            borderWidth: 0,
            pointPadding: 0,
            borderColor: 'none',
        },
    };
}

// ─── Component ───
class AvailabilityFlapping extends Component {
    constructor(props) {
        super(props);
        this._fetchSeq = 0;
        this.state = {
            isLoading: true,
            isLoadingNodes: false,
            dataError: false,
            loadError: false,
            chartOptions: null,
            nodeChartOptions: null,
            nodePercentages: null,
            percentages: null,
            pilesCount: 0,
            hiddenSeries: {},
            showNodes: false,
            rawApiData: null,
            flindexConfigFingerprint: '',
            period: 'd',
            typePeriod: 1,
            statePeriod: { startDate: new Date(), endDate: new Date(), key: 'selection' },
            periodEl: null,
            settingsEl: null,
            isInfo: null,
            checkedA: Boolean(this.props.automaticUpdate),
            dateMoment: moment().locale('es').format('DD/MM/YYYY, HH:mm:ss'),
            intervalId: null,
        };
    }

    componentDidMount() {
        this.getDataFromAPI();
        this.syncAutoRefreshInterval();
        window.addEventListener(FLINDEX_CONFIG_CHANGED_EVENT, this.handleFlindexConfigChanged);
        window.addEventListener('storage', this.handleFlindexConfigStorage);
    }

    syncAutoRefreshInterval = () => {
        if (this.state.intervalId) {
            clearInterval(this.state.intervalId);
        }
        if (this.state.checkedA) {
            const intervalId = setInterval(
                () => this.getDataFromAPI(this.state.showNodes),
                this.props.refreshTime || REFRESH_TIME
            );
            this.setState({ intervalId });
        } else {
            this.setState({ intervalId: null });
        }
    };

    componentDidUpdate(prevProps, prevState) {
        const prevObjId = prevProps.match?.params?.objetivo_id;
        const nextObjId = this.props.match?.params?.objetivo_id;
        if (prevObjId !== nextObjId) {
            this.setState({
                showNodes: false,
                nodeChartOptions: null,
                nodePercentages: null,
                rawApiData: null,
                flindexConfigFingerprint: '',
            }, () => this.getDataFromAPI(false));
        }
        if (prevState.period !== this.state.period ||
            prevState.statePeriod !== this.state.statePeriod) {
            this.setState({
                rawApiData: null,
                flindexConfigFingerprint: '',
                nodeChartOptions: null,
                nodePercentages: null,
            }, () => this.getDataFromAPI(this.state.showNodes));
        }
        if (prevProps.automaticUpdate !== this.props.automaticUpdate) {
            this.setState(
                { checkedA: Boolean(this.props.automaticUpdate) },
                () => this.syncAutoRefreshInterval()
            );
        } else if (prevProps.refreshTime !== this.props.refreshTime && this.state.checkedA) {
            this.syncAutoRefreshInterval();
        } else if (prevState.checkedA !== this.state.checkedA) {
            this.syncAutoRefreshInterval();
        }
        // Detalle por nodo: no recalcular consolidado si ya estaba cargado (mismo período).
        if (!prevState.showNodes && this.state.showNodes) {
            const nodos = this.state.rawApiData?.nodos;
            if (nodos && Object.keys(nodos).length > 0) {
                this.processNodeData(this.state.rawApiData);
            } else {
                this.getDataFromAPI(true, { preserveConsolidado: true });
            }
        }
        if (prevState.showNodes && !this.state.showNodes) {
            this.setState({ nodeChartOptions: null, nodePercentages: null });
        }
    }

    componentWillUnmount() {
        if (this.state.intervalId) clearInterval(this.state.intervalId);
        window.removeEventListener(FLINDEX_CONFIG_CHANGED_EVENT, this.handleFlindexConfigChanged);
        window.removeEventListener('storage', this.handleFlindexConfigStorage);
    }

    handleFlindexConfigChanged = (event) => {
        const { objetivo_id } = this.props.match.params;
        const changedId = event?.detail?.objetivoId;
        if (!changedId || String(changedId) !== String(objetivo_id)) {
            return;
        }
        this.setState({
            rawApiData: null,
            flindexConfigFingerprint: '',
            nodeChartOptions: null,
            nodePercentages: null,
        }, () => this.getDataFromAPI(this.state.showNodes));
    };

    handleFlindexConfigStorage = (event) => {
        if (event.key !== FLINDEX_CONFIG_STORAGE_KEY || !event.newValue) {
            return;
        }
        try {
            const detail = JSON.parse(event.newValue);
            this.handleFlindexConfigChanged({ detail });
        } catch (e) {
            // ignore invalid payload
        }
    };

    handleShowSettings = (event) => {
        this.setState({ settingsEl: event.currentTarget });
    };

    handleCloseSettings = () => {
        this.setState({ settingsEl: null });
    };

    handleShowIsInfo = (event) => {
        this.setState({ isInfo: event.currentTarget });
    };

    handleCloseIsInfo = () => {
        this.setState({ isInfo: null });
    };

    handleMenuRefresh = () => {
        this.setState({ settingsEl: null });
        this.getDataFromAPI(this.state.showNodes);
    };

    handleAutoRefreshSwitch = (event) => {
        this.setState({ checkedA: event.target.checked });
    };

    handleRetry = () => {
        this.getDataFromAPI(this.state.showNodes);
    };

    getDataFromAPI = (includeNodes = false, options = {}) => {
        const { preserveConsolidado = false } = options;
        const fetchSeq = ++this._fetchSeq;

        if (includeNodes) {
            this.setState({ isLoadingNodes: true, loadError: false });
        } else {
            this.setState({ isLoading: true, loadError: false });
        }
        const { objetivo_id } = this.props.match.params;
        const { period, statePeriod } = this.state;
        const token = sessionStorage.getItem('key');
        const user = sessionStorage.getItem('user');
        const node = 0;

        let fetchPromise;
        if (period === 'c') {
            const fechaInicio = statePeriod.startDate instanceof Date
                ? statePeriod.startDate.toISOString().substring(0, 10)
                : String(statePeriod.startDate).substring(0, 10);
            const fechaTermino = statePeriod.endDate instanceof Date
                ? moment(statePeriod.endDate).format().substring(0, 10)
                : String(statePeriod.endDate).substring(0, 10);
            fetchPromise = getFlindexCustom(
                user, objetivo_id, node, fechaInicio, fechaTermino, token, 0, includeNodes
            );
        } else {
            fetchPromise = getFlindex(
                user, objetivo_id, period, node, token, includeNodes
            );
        }

        fetchPromise
            .then(res => {
                if (!res.ok && res.status !== 401) throw new Error(`HTTP error ${res.status}`);
                return res.json();
            })
            .then(data => {
                if (fetchSeq !== this._fetchSeq) return;
                if (data.status == 1 || data.status == 5) {
                    localStorage.setItem('isAuthenticated', false);
                    this.setState({ isLoading: false, isLoadingNodes: false });
                    return;
                }
                try {
                    this.processData(data, { preserveConsolidado });
                } catch (error) {
                    this.setState({ dataError: true, isLoading: false, isLoadingNodes: false });
                }
            })
            .catch((error) => {
                if (fetchSeq !== this._fetchSeq) return;
                reportFetchError('AvailabilityFlapping', error);
                this.setState({ loadError: true, isLoading: false, isLoadingNodes: false });
            });
    }

    processData = (apiData, options = {}) => {
        const { preserveConsolidado = false } = options;
        const incomingFp = flindexConfigFingerprint(apiData.config);
        const sameConfig =
            incomingFp &&
            incomingFp === this.state.flindexConfigFingerprint;
        let consolidado = apiData.consolidado;

        // Solo reutilizar consolidado al abrir nodos si la config no cambió.
        if (preserveConsolidado && sameConfig && this.state.rawApiData?.consolidado) {
            consolidado = this.state.rawApiData.consolidado;
        }

        if (!consolidado || !consolidado.segments) {
            this.setState({ dataError: true, isLoading: false, isLoadingNodes: false });
            return;
        }

        const { segments, percentages, piles_count } = consolidado;

        const series = buildSeries(segments, 0, 40);

        const chartOptions = {
            chart: { type: 'xrange', height: 120 },
            title: null,
            credits: { enabled: false },
            xAxis: makeXAxisConfig(),
            yAxis: {
                categories: ['Consolidado'],
                title: null,
                gridLineWidth: 0,
                reversed: true,
                labels: {
                    useHTML: true,
                    style: { whiteSpace: 'normal' },
                    step: 1,
                    formatter: function () {
                        return '<div align="left" style="word-wrap:break-word;word-break:break-all;width:150px">' + this.value + '</div>';
                    },
                },
            },
            series,
            plotOptions: makePlotOptions(),
            legend: { enabled: false },
            tooltip: makeTooltipConfig(),
        };

        const mergedApiData = {
            ...apiData,
            consolidado,
            nodos: apiData.nodos
                || (preserveConsolidado && this.state.rawApiData?.nodos)
                || {},
        };

        const newState = {
            isLoading: false,
            isLoadingNodes: false,
            dataError: false,
            chartOptions,
            percentages,
            pilesCount: piles_count,
            rawApiData: mergedApiData,
            flindexConfigFingerprint: incomingFp || this.state.flindexConfigFingerprint,
            dateMoment: moment().locale('es').format('DD/MM/YYYY, HH:mm:ss'),
        };

        this.setState(newState, () => {
            if (this.state.showNodes) {
                this.processNodeData(mergedApiData);
            }
        });
    }

    processNodeData = (apiData) => {
        const nodos = apiData.nodos;
        if (!nodos) {
            this.setState({ nodeChartOptions: null, nodePercentages: null });
            return;
        }

        const nodoNames = Object.keys(nodos).sort();
        const categories = [];
        let allSeries = [];
        const nodePercentages = {};

        nodoNames.forEach((name, idx) => {
            categories.push(name);
            const { segments, percentages } = nodos[name];
            if (percentages) nodePercentages[name] = percentages;
            const series = buildSeries(segments, idx, 20);
            allSeries = allSeries.concat(series);
        });

        const nodeChartOptions = {
            chart: { type: 'xrange', height: 80 + categories.length * 70 },
            title: null,
            credits: { enabled: false },
            xAxis: makeXAxisConfig(),
            yAxis: {
                categories,
                title: null,
                gridLineWidth: 0,
                reversed: true,
                labels: {
                    useHTML: true,
                    style: { whiteSpace: 'normal' },
                    step: 1,
                    formatter: function () {
                        const name = this.value;
                        const p = nodePercentages[name];
                        let html = '<div style="width:170px;line-height:1.3">';
                        html += '<div style="word-wrap:break-word;word-break:break-all;font-weight:500">' + name + '</div>';
                        if (p) {
                            html += '<div style="font-size:10px;margin-top:2px">';
                            html += '<span style="color:' + FLINDEX_COLORS.uptime + ';font-weight:600">' + p.uptime + '%</span>';
                            html += ' <span style="color:#aaa">|</span> ';
                            html += '<span style="color:' + FLINDEX_COLORS.downtime + ';font-weight:600">' + p.downtime + '%</span>';
                            html += ' <span style="color:#aaa">|</span> ';
                            html += '<span style="color:' + FLINDEX_COLORS.flapping + ';font-weight:600">' + p.flapping + '%</span>';
                            html += '</div>';
                        }
                        html += '</div>';
                        return html;
                    },
                },
            },
            series: allSeries,
            plotOptions: makePlotOptions(),
            legend: { enabled: false },
            tooltip: makeTooltipConfig(),
        };

        this.setState({ nodeChartOptions, nodePercentages });
    }

    chartRef = null;

    toggleSeries = (seriesName) => {
        this.setState(prev => {
            const hiddenSeries = { ...prev.hiddenSeries };
            hiddenSeries[seriesName] = !hiddenSeries[seriesName];
            return { hiddenSeries };
        }, () => {
            if (this.chartRef) {
                const chart = this.chartRef.chart;
                chart.series.forEach(s => {
                    if (s.name === seriesName) {
                        if (this.state.hiddenSeries[seriesName]) {
                            s.hide();
                        } else {
                            s.show();
                        }
                    }
                });
            }
        });
    }

    handlePeriodClick = (prevPeriod, prevTypeGraph, dateSelection, nextPeriod) => {
        const periodMap = {
            hora: 'h', '3hora': '3h', dia: 'd', '3dia': '3d', semana: 'w', custom: 'c'
        };
        const labelMap = {
            hora: 0, '3hora': 4, dia: 1, '3dia': 5, semana: 2, custom: 3
        };
        const newState = {
            period: periodMap[nextPeriod] || 'd',
            typePeriod: labelMap[nextPeriod] !== undefined ? labelMap[nextPeriod] : 1,
            periodEl: null,
        };
        if (nextPeriod === 'custom' && dateSelection) {
            newState.statePeriod = dateSelection;
        }
        this.setState(newState);
    }

    render() {
        const { classes, t } = this.props;
        const {
            isLoading, dataError, loadError, chartOptions, nodeChartOptions,
            percentages, pilesCount, showNodes, isLoadingNodes,
            typePeriod, period, statePeriod, periodEl, dateMoment,
            settingsEl, isInfo, checkedA,
        } = this.state;

        const namePeriodPortlet = [
            t('period.text.last-hour', 'ÚLTIMA HORA'),
            t('period.text.last-day', 'ÚLTIMO DÍA'),
            t('period.text.last-week', 'ÚLTIMA SEMANA'),
            t('period.text.custom', 'CUSTOM'),
            t('period.text.last-3-hours', 'ÚLTIMAS 3 HORAS'),
            t('period.text.last-3-days', 'ÚLTIMOS 3 DÍAS'),
        ];
        const showPeriodEl = Boolean(periodEl);
        const showSettingsEl = Boolean(settingsEl);
        const showIsInfo = Boolean(isInfo);

        return (
            <Portlet>
                <PortletHeader className={classes.header}>
                    <PortletLabel
                        title={t('flindexCard.title')}
                    />
                    <PortletToolbar>
                        <IconButton onClick={this.handleShowSettings} aria-label="settings">
                            <SettingsOutlinedIcon />
                        </IconButton>
                        <Popover
                            anchorEl={settingsEl}
                            open={showSettingsEl}
                            onClose={this.handleCloseSettings}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            <IconButton className={classes.refreshButton} onClick={this.handleMenuRefresh}>
                                <div className={classes.settingsMenuRow}>
                                    <RefreshIcon color="action" />
                                    <Typography className={classes.settingsMenuLabel}>
                                        {t('text.update', 'Actualizar')}
                                    </Typography>
                                </div>
                            </IconButton>
                            <IconButton onClick={this.handleShowIsInfo}>
                                <div className={classes.settingsMenuRow}>
                                    <HelpIcon color="action" />
                                    <Typography className={classes.settingsMenuLabel}>
                                        {t('availability.information.tooltip', 'Información')}
                                    </Typography>
                                </div>
                            </IconButton>
                            <Popover
                                anchorEl={isInfo}
                                open={showIsInfo}
                                onClose={this.handleCloseIsInfo}
                                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                            >
                                <PortletContent className={classes.infoPopoverContent}>
                                    {t('flindexCard.settings.infoBody')}
                                </PortletContent>
                            </Popover>
                            <Tooltip title={t('text.auto-update', 'Actualización automática')}>
                                <div style={{ textAlign: 'center' }}>
                                    <Switch
                                        checked={checkedA}
                                        onChange={this.handleAutoRefreshSwitch}
                                        color="primary"
                                        inputProps={{ 'aria-label': t('text.auto-update', 'Actualización automática') }}
                                    />
                                </div>
                            </Tooltip>
                        </Popover>
                    </PortletToolbar>
                </PortletHeader>
                <PortletContent className={classes.content}>
                    {isLoading ? (
                        <div className={classes.loadingContainer}>
                            <CircularProgress variant="indeterminate" size={24} thickness={4} />
                            <br />
                            <Typography variant="h5">
                                {t('text.loading', 'Cargando...')}
                            </Typography>
                        </div>
                    ) : loadError ? (
                        <Box textAlign="center">
                            <Typography>{t('text.load-error')}</Typography>
                            <Button color="primary" onClick={this.handleRetry}>{t('text.retry')}</Button>
                        </Box>
                    ) : dataError ? (
                        <Typography color="textSecondary" className={classes.noData}>
                            {t('text.no-data')}
                        </Typography>
                    ) : (
                        <>
                            {percentages && (
                                <div className={classes.metricsRow}>
                                    {[
                                        { key: 'uptime', label: 'Uptime', color: FLINDEX_COLORS.uptime },
                                        { key: 'downtime', label: 'Downtime', color: FLINDEX_COLORS.downtime },
                                        { key: 'flapping', label: 'Flapping', color: FLINDEX_COLORS.flapping },
                                    ].map(item => (
                                        <div key={item.key} className={classes.metricCard}>
                                            <Typography className={classes.metricValue} style={{ color: item.color }}>
                                                {percentages[item.key]}%
                                            </Typography>
                                            <Typography className={classes.metricLabel}>
                                                {item.label}
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <PerfectScrollbar>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptions}
                                    ref={ref => { this.chartRef = ref; }}
                                />
                            </PerfectScrollbar>

                            {percentages && (
                                <div className={classes.summary}>
                                    {[
                                        { key: 'uptime', name: 'Uptime', color: FLINDEX_COLORS.uptime },
                                        { key: 'downtime', name: 'Downtime', color: FLINDEX_COLORS.downtime },
                                        { key: 'flapping', name: 'Flapping', color: FLINDEX_COLORS.flapping },
                                    ].map(item => {
                                        const hidden = this.state.hiddenSeries[item.name];
                                        return (
                                            <span
                                                key={item.key}
                                                className={classes.summaryItem}
                                                onClick={() => this.toggleSeries(item.name)}
                                                style={{ opacity: hidden ? 0.35 : 1, textDecoration: hidden ? 'line-through' : 'none' }}
                                            >
                                                <span className={classes.summaryDot} style={{ backgroundColor: item.color }} />
                                                {item.name}: {percentages[item.key]}%
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            <div className={classes.nodeToggle}>
                                <Typography variant="body2" color="textSecondary" className={classes.nodeToggleLabel}>
                                    {t('flindex.chart.nodeDetailSwitch')}
                                </Typography>
                                <Switch
                                    checked={showNodes}
                                    onChange={() => this.setState({ showNodes: !showNodes })}
                                    size="small"
                                    color="primary"
                                />
                            </div>

                            <Collapse in={showNodes}>
                                {showNodes && isLoadingNodes && (
                                    <div className={classes.loadingContainer}>
                                        <CircularProgress variant="indeterminate" size={20} thickness={4} />
                                        <Typography variant="body2" color="textSecondary">
                                            {t('flindex.chart.loadingNodes')}
                                        </Typography>
                                    </div>
                                )}
                                {showNodes && nodeChartOptions && (
                                    <PerfectScrollbar>
                                        <HighchartsReact highcharts={Highcharts} options={nodeChartOptions} />
                                    </PerfectScrollbar>
                                )}
                            </Collapse>

                            <div className={classes.updateRow}>
                                <div className={classes.updateInner}>
                                    <Avatar variant="rounded" className={classes.updateAvatar}>
                                        <UpdateIcon className={classes.updateIcon} />
                                    </Avatar>
                                    <Typography className={classes.updateText}>
                                        &nbsp; {t('text.last-update', 'Última actualización')}: {dateMoment}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    )}
                </PortletContent>
                <PortletFooter className={classes.portletFooter}>
                    <Button
                        className={classes.periodButton}
                        onClick={(e) => this.setState({ periodEl: e.currentTarget })}
                    >
                        {namePeriodPortlet[typePeriod]}<ArrowDropDownIcon />
                    </Button>
                    <Popover
                        anchorEl={periodEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        onClose={() => this.setState({ periodEl: null })}
                        open={showPeriodEl}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Period onClick={this.handlePeriodClick} range={statePeriod} period={period} isOneHour={true} />
                    </Popover>
                </PortletFooter>
            </Portlet>
        );
    }
}

export default compose(withRouter, withStyles(styles), withTranslation())(AvailabilityFlapping);
